import React, { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';
import { Color } from 'antd/es/color-picker';

// Shared UI Library Components
import {
  ColorPickerItem,
  CustomButton,
  CustomIcon,
  CustomTooltip,
  EIconName
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './theme-settings.scss';

// Store entities
import { updateWidgetTheme } from '../../../../../../store/widget-settings/widget-settings.thunks';

// Models
import { IThemeSettings } from '../../../../../../interfaces/widget-settings.interfaces';

// Config
import { CThemeDefaultColors } from '../../../../../../constantes/theme.constants';

type TFormValue = {
  primary_accent_color: string | Color,
  primary_accent_second_color: string | Color,
  primary_system_color: string | Color,
  elemental_header_color: string | Color,
  elemental_call_color: string | Color,
  secondary_success_color: string | Color,
  secondary_error_color: string | Color,
  secondary_warning_color: string | Color,
};

type TThemeSettingsProps = {
  theme_settings: IThemeSettings;
  loading?: boolean;
};

export const ThemeSettings: FC<TThemeSettingsProps> = (
  { theme_settings, loading = false }: TThemeSettingsProps
) => {
  const [form] = Form.useForm<TFormValue>();
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [notDefault, setNotDefault] = useState<boolean>(true);

  const dispatch = useDispatch<any>();

  const successPresets = [
    {
      label: 'Recommended Colors',
      colors: ['#21917F', '#047857', '#1B5E20', '#33691E', '#2E7D32', '#00796B', '#0D9488', '#059669', '#16A34A', '#65A30D', '#10B981', '#14B8A6'],
      defaultOpen: true
    }
  ];

  const errorPresets = [
    {
      label: 'Recommended Colors',
      colors: ['#C41A1E', '#D81B60', '#DC2626', '#DD2C00', '#D32F2F', '#D50000', '#C62828', '#BF360C', '#B91C1C', '#B71C1C'],
      defaultOpen: true
    }
  ];

  const warningPresets = [
    {
      label: 'Recommended Colors',
      colors: ['#EAB30B', '#FF6F00', '#F97316', '#FF8F00', '#F2871F', '#FB923C', '#ED811D', '#F9A825', '#F59E0B', '#FFAB00', '#FBC02D', '#FBBF24', '#FACC15'],
      defaultOpen: true
    }
  ];

  const panelRender = (panel: React.ReactNode, extra: {
    components: { Picker: FC; Presets: FC }
  }) => {
    const Presets = extra.components.Presets
    return <Presets/>;
  }

  const parseObjectColors = (value: TFormValue): { [key in keyof TFormValue]: string } => {
    return Object.keys(value)
      .reduce((acc, colorName) => (
        {
          ...acc,
          [colorName]: typeof value[colorName as keyof TFormValue] === 'string' ?
            value[colorName as keyof TFormValue] : `#${ (value[colorName as keyof TFormValue] as Color).toHex() }`
        }
      ), {} as { [key in keyof TFormValue]: string });
  }

  const compareHasChangesObjects = (oldObj: { [key in keyof TFormValue]: string }, newObj: { [key in keyof TFormValue]: string }): boolean => {
    return Object.keys(oldObj).length !== Object.keys(newObj).length ||
      Object.keys(oldObj).some((key) => oldObj[key as keyof TFormValue] !== newObj[key as keyof TFormValue]);
  }

  const onValuesChange = (_: { [key in keyof TFormValue]: string | Color }, allValues: TFormValue): void => {
    const parsedColors = parseObjectColors(allValues);

    setNotDefault(!compareHasChangesObjects(CThemeDefaultColors, parsedColors));
    setHasChanges(compareHasChangesObjects(theme_settings, parsedColors));
  }

  const applyChanges = async (): Promise<void> => {
    const formValue = form.getFieldsValue();
    const parsedColors = parseObjectColors(formValue);

    const { payload } = await dispatch(updateWidgetTheme(parsedColors as IThemeSettings));
    if (!payload) return;

    setHasChanges(false);
  }

  const defaultSettings = async (): Promise<void> => {
    const { payload } = await dispatch(updateWidgetTheme(CThemeDefaultColors as IThemeSettings));
    if (!payload) return;

    setHasChanges(false);
  }

  useEffect(() => {
    form.setFieldsValue(theme_settings);
    setNotDefault(!compareHasChangesObjects(CThemeDefaultColors, theme_settings));
  }, [theme_settings]);

  return (
    <div className="theme-settings">
      <div className="theme-settings--header">
        <div className="header-title">
          Theme Settings
        </div>
        <div className="header-actions">
          <CustomButton size={ 'small' } disabled={ notDefault || loading } onClick={ defaultSettings }>
            Default Settings
          </CustomButton>
          <CustomButton size={ 'small' } disabled={ !hasChanges || loading } onClick={ applyChanges }>
            Apply Changes
          </CustomButton>
        </div>
      </div>
      <div className="theme-settings--message">
        <div className="message-icon">
          <CustomIcon size={ 'size-xl-32' } name={ EIconName.Info }/>
        </div>
        <div className="message-text">
          To ensure compliance with the Web Content Accessibility Guidelines (WCAG), it is recommended to check the
          color contrast for the colors you choose. You can use a color contrast checker tool to verify the contrast
          ratio between foreground and background colors. Here is an example of a color contrast checker tool: <a
          href="https://webaim.org/resources/contrastchecker/"
          target="_blank">https://webaim.org/resources/contrastchecker/</a>.
        </div>
      </div>
      <Form form={ form } onValuesChange={ onValuesChange } className="theme-settings--palettes">
        <div className="palettes-item">
          <div className="palettes-item--title">
            Primary Palette
          </div>
          <div className="palettes-item--colors">
            <Form.Item name="primary_system_color" initialValue={ CThemeDefaultColors.primary_system_color }>
              <ColorPickerItem label={ 'System' } placement={ 'bottom' } infoTooltip={ 'System' } defaultFormat={ 'hex' }/>
            </Form.Item>

            <Form.Item name="primary_accent_color" initialValue={ CThemeDefaultColors.primary_accent_color }>
              <ColorPickerItem label={ 'Accent' } placement={ 'bottom' } infoTooltip={ 'Accent' }/>
            </Form.Item>

            <Form.Item name="primary_accent_second_color"
                       initialValue={ CThemeDefaultColors.primary_accent_second_color }>
              <ColorPickerItem label={ 'Accent 2' } placement={ 'bottom' } infoTooltip={ 'Accent 2' }/>
            </Form.Item>
          </div>
        </div>
        <div className="palettes-item">
          <div className="palettes-item--title">
            Element Palette
          </div>
          <div className="palettes-item--colors">
            <Form.Item name="elemental_header_color" initialValue={ CThemeDefaultColors.elemental_header_color }>
              <ColorPickerItem label={ 'Title' } placement={ 'bottom' } infoTooltip={ 'Title' }/>
            </Form.Item>

            <Form.Item name="elemental_call_color" initialValue={ CThemeDefaultColors.elemental_call_color }>
              <ColorPickerItem label={ 'Call BG' }  placement={ 'bottom' } infoTooltip={ 'Call BG' }/>
            </Form.Item>
          </div>
        </div>
        <div className="palettes-item">
          <div className="palettes-item--title">
            Secondary Palette
          </div>
          <div className="palettes-item--colors">

            <Form.Item name="secondary_success_color" initialValue={ CThemeDefaultColors.secondary_success_color }>
              <ColorPickerItem label={ 'Success' } placement={ 'bottom' } presets={ successPresets }
                               panelRender={ panelRender } infoTooltip={ 'Success' }/>
            </Form.Item>

            <Form.Item name="secondary_error_color" initialValue={ CThemeDefaultColors.secondary_error_color }>
              <ColorPickerItem label={ 'Error' } placement={ 'bottom' } presets={ errorPresets }
                               panelRender={ panelRender } infoTooltip={ 'Error' }/>
            </Form.Item>

            <Form.Item name="secondary_warning_color" initialValue={ CThemeDefaultColors.secondary_warning_color }>
              <ColorPickerItem label={ 'Warning' } placement={ 'bottom' } presets={ warningPresets }
                               panelRender={ panelRender } infoTooltip={ 'Warning' }/>
            </Form.Item>
          </div>
        </div>
      </Form>
    </div>
  );
}
